import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreValue } from '@/lib/tinyStore';
import { applyBadgeCount } from '../badge';
import { dismissDesktopNotification } from '../desktopNotifications';
import { ingestNotification } from '../ingest';
import { notificationApi } from '../notificationsClient';
import {
  notificationActions, notificationStore, selectItems, selectLoading, selectUnread,
  selectUnseen,
} from '../notificationStore';
import type { NotificationItem } from '../types';

/** Navigates to a notification's deep link and marks it read. */
export function useActivateNotification(): (item: NotificationItem) => void {
  const navigate = useNavigate();

  return useCallback(
    (item: NotificationItem) => {
      notificationActions.markRead(item.id);
      void notificationApi.markRead(item.id).catch(() => undefined);
      /* Read means read EVERYWHERE. A native toast outlives the click that
         opened it — it times out into the Action Center and sits there — so
         opening a notification from the Activity feed has to retract the OS
         copy too, or the shade keeps offering something already dealt with. */
      dismissDesktopNotification(item.id);
      if (item.deepLink) navigate(item.deepLink);
    },
    [navigate],
  );
}

/**
 * Hydrates the centre once and keeps it live over the gateway.
 * Mount exactly once (NotificationRuntime) — every other consumer reads
 * the store.
 */
export function useNotificationFeed(): void {
  const activate = useActivateNotification();

  useEffect(() => {
    let alive = true;
    void notificationApi
      .list({ limit: 50 })
      .then((page) => {
        if (alive) notificationActions.hydrate(page.items);
      })
      .catch(() => {
        if (alive) notificationActions.fail();
      });

    const unsubscribe = notificationApi.subscribe((item, delivery) =>
      ingestNotification(item, { onActivate: activate }, delivery),
    );
    /* The other half of the same stream: rows the server has withdrawn because
       they stopped being true. Without this a ring stayed in the bell until the
       next full refetch even though the server had already deleted it. */
    const unsubscribeDismissals = notificationApi.subscribeDismissals((ids, groupKey) => {
      notificationActions.withdraw(ids, groupKey);
      /* The server has decided these rows no longer hold — a ring answered on
         another device, a mention in a deleted message. Whatever the OS is
         still showing about them has to go with them, or the Action Center
         keeps offering something the app has already retracted. */
      for (const id of ids) dismissDesktopNotification(id);
    });

    return () => {
      alive = false;
      unsubscribe();
      unsubscribeDismissals();
    };
  }, [activate]);
}

export function useNotificationItems(): readonly NotificationItem[] {
  return useStoreValue(notificationStore, selectItems);
}

export function useUnseenCount(): number {
  return useStoreValue(notificationStore, selectUnseen);
}

export function useUnreadCount(): number {
  return useStoreValue(notificationStore, selectUnread);
}

export function useNotificationsLoading(): boolean {
  return useStoreValue(notificationStore, selectLoading);
}

/**
 * Keeps the OS badge and the tab title in step with the UNSEEN count.
 *
 * IT USED TO READ `unread`, AND THAT IS WHY THE TITLE SAID "(45)".
 * The two counters mean different things and 010 says so at the column:
 * `is_seen` is "badge cleared, row not opened", `is_read` is "opened".
 * `routes/notifications.ts` opens with "The badge is a partial-index COUNT over
 * NOT is_seen rows only", ships `unseen` beside `unread` for exactly this, and
 * `POST /notifications/seen` exists so that OPENING THE BELL drops the badge to
 * zero. Reading `unread` here defeated all three: the number could only ever
 * fall by clicking every individual row, so it ratcheted upward for ever.
 * On the live workspace the owner is unseen 43 / unread 45 — and 22 of those
 * were rings for calls that had already ended.
 */
export function useBadgeSync(): void {
  const unseen = useUnseenCount();
  useEffect(() => {
    applyBadgeCount(unseen);
  }, [unseen]);
}
